import { useEffect, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { Link, useLocation } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { showInkToast } from '../cms/InkToast';
import { askInk } from '../cms/InkConfirm';
import { ChannelLogo } from '../reach/ChannelLogo';
import { channelName, isPersonalProfile, SET_APPROVAL_MODE } from '../reach/reach';
import { SET_AGENT_PERMISSION } from './AgentRules';
import {
  agentAnchor,
  agentSkipLine,
  APPROVALS_PATH,
  ApprovalMode,
  ASK_WORDS,
  channelAnchor,
  GOES_OUT_WITHOUT_YOU,
  GoesOutAction,
  GoesOutChannel,
  goesOutList,
  goesOutSentence,
  modeWords,
  PERM_ACTIVITY_PATH,
  ruleAnchor,
  useGoesOut,
} from './goesOut';

/**
 * Account → Approvals, the one place that answers "Can this go out without me?"
 * (backlog 594cb5eb, app/Approvals Single Home): agents, channels and rules, each
 * with its setting, and one switch for all of them. Everywhere else shows a chip
 * that links here. The switch is worked out from that state on the server, never
 * stored. Admins change these; everyone else sees them.
 */

const REQUIRE_APPROVAL = gql`
  mutation RequireApprovalForEverything($accountId: ID!) {
    requireApprovalForEverything(accountId: $accountId) {
      snapshotId
    }
  }
`;

const RESTORE_SNAPSHOT = gql`
  mutation RestoreApprovalSnapshot($snapshotId: ID!) {
    restoreApprovalSnapshot(snapshotId: $snapshotId)
  }
`;

const SET_RULE_APPROVAL = gql`
  mutation SetRuleApproval($id: ID!, $mode: String!) {
    setRuleApproval(id: $id, mode: $mode)
  }
`;

/** An agent's card on AI Agents. */
export const agentCardId = (dataSourceId: string) => `agent-card-${dataSourceId}`;

/** The current setting, read-only, opening Approvals at its row. */
export function ApprovalChip({
  mode,
  to,
  suffix = '',
}: {
  mode: ApprovalMode;
  to: string;
  suffix?: string;
}) {
  return (
    <Link className="agc-chip" to={to} title="Change in Approvals">
      <span className="ink-dot agc-chip-dot" data-mode={mode} aria-hidden />
      {modeWords(mode)}
      {suffix}
    </Link>
  );
}

/** The one line, on Today and AI Agents, with the way to the list. */
export function GoesOutSentence({
  accountId,
  boxed = false,
}: {
  accountId: string;
  boxed?: boolean;
}) {
  const { goesOut } = useGoesOut(accountId);
  if (!goesOut) return null;
  const counted = goesOut.items.some((i) => i.kind !== 'campaign');
  return (
    <div className="agc-summary" data-boxed={boxed ? 'true' : undefined}>
      <span className="ink-dot agc-dot" data-tone={counted ? 'warn' : 'ok'} aria-hidden />
      <span className="agc-summary-text">{goesOutSentence(goesOut.items)}</span>
      <Link to={APPROVALS_PATH}>
        Approvals <span aria-hidden>→</span>
      </Link>
    </div>
  );
}

/** AI Agents, just above Connected agents. */
export function AgentSkipSummary({ accountId }: { accountId: string }) {
  return <GoesOutSentence accountId={accountId} boxed />;
}

/** An agent's card, under its meta line: what goes out without asking. */
export function AgentSkipLine({ rules }: { rules?: Parameters<typeof agentSkipLine>[0] }) {
  const line = agentSkipLine(rules);
  return line ? (
    <div className="agc-skip-line">
      <span className="ink-dot agc-skip-dot" aria-hidden />
      {line}
    </div>
  ) : null;
}

const MODES: ApprovalMode[] = ['ask', 'out'];

function ModePair({
  label,
  mode,
  disabled,
  canChange,
  onPick,
}: {
  label: string;
  mode: ApprovalMode;
  disabled: boolean;
  canChange: boolean;
  onPick: (next: ApprovalMode) => void;
}) {
  if (!canChange) {
    return (
      <span className="agc-chip" data-static="true">
        <span className="ink-dot agc-chip-dot" data-mode={mode} aria-hidden />
        {modeWords(mode)}
      </span>
    );
  }
  return (
    <div className="agr-seg agc-pair" role="radiogroup" aria-label={label}>
      {MODES.map((value) => (
        <button
          type="button"
          key={value}
          role="radio"
          aria-checked={mode === value}
          data-on={mode === value ? 'true' : undefined}
          disabled={disabled}
          onClick={() => {
            if (mode !== value) onPick(value);
          }}
        >
          {mode === value ? (
            <span className="ink-dot agc-chip-dot" data-mode={value} aria-hidden />
          ) : null}
          {modeWords(value)}
        </button>
      ))}
    </div>
  );
}

const AGENT_ACTIONS: Array<{ key: GoesOutAction; name: string; desc: string }> = [
  { key: 'publish', name: 'Publish & schedule', desc: 'Articles, pages and forms' },
  { key: 'surface', name: 'Change the site surface', desc: 'Look, header, footer and home page' },
  { key: 'delete', name: 'Delete content', desc: 'Unpublishing is always allowed' },
];

function agentDesc(key: GoesOutAction, base: string, mode: string) {
  if (mode !== 'allow') return base;
  return key === 'publish'
    ? 'Publishes after you OK that version in the chat, with no check in tinycms.'
    : 'You OK it in the chat, with no check in tinycms.';
}

const channelDesc = (c: GoesOutChannel) =>
  !c.publishing
    ? 'Publishing is off, so nothing goes out to it. Turn it on in Social → Channels.'
    : c.mode === 'out'
      ? 'Your agent’s posts go out on time, without asking.'
      : 'Each post waits for your OK.';

const initials = (name: string) =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase();

export function ApprovalsSettings() {
  const { accountId, selectedAccount } = useAccount();
  const site = selectedAccount?.name || 'this site';
  const { goesOut } = useGoesOut(accountId);
  const { hash } = useLocation();
  const refetchQueries = [GOES_OUT_WITHOUT_YOU];
  const [requireApproval, { loading: tightening }] = useMutation(REQUIRE_APPROVAL, {
    refetchQueries,
  });
  const [restore, { loading: restoring }] = useMutation(RESTORE_SNAPSHOT, { refetchQueries });
  const [setAgent] = useMutation(SET_AGENT_PERMISSION, { refetchQueries });
  const [setChannel] = useMutation(SET_APPROVAL_MODE, { refetchQueries });
  const [setRule] = useMutation(SET_RULE_APPROVAL, { refetchQueries });
  const [busy, setBusy] = useState<string | null>(null);

  // A chip elsewhere opens this page at its row, lit for a moment.
  const anchor = hash.replace(/^#/, '');
  const [lit, setLit] = useState('');
  const loaded = Boolean(goesOut);
  useEffect(() => {
    if (!anchor || !loaded) return undefined;
    document.getElementById(anchor)?.scrollIntoView({ block: 'center' });
    setLit(anchor);
    const t = window.setTimeout(() => setLit(''), 2400);
    return () => window.clearTimeout(t);
  }, [anchor, loaded]);

  const heading = (
    <>
      <h1 className="ink-h1">Approvals</h1>
      <p className="ink-lede">
        Everything that can go out on {site}, and whether it asks you first. This is the only place
        to change it.
      </p>
    </>
  );
  if (!goesOut) return heading;
  const { items, agents, channels, rules, social, allRequireApproval, snapshotId, canChange } =
    goesOut;
  const campaigns = items.filter((i) => i.kind === 'campaign');
  const counted = items.some((i) => i.kind !== 'campaign');

  const run = async (key: string, fn: () => Promise<unknown>, done: string) => {
    setBusy(key);
    try {
      await fn();
      showInkToast(done);
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t save. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const pickChannel = async (c: GoesOutChannel, mode: ApprovalMode) => {
    const name = channelName(c.platform);
    const who = c.name || c.username || name;
    // Posting under a person's own name is worth a second look.
    if (
      mode === 'out' &&
      isPersonalProfile(c.platform, c.profileId) &&
      !(await askInk({
        title: `This will post as you (${who}), not as ${site}.`,
        body: 'Posts go out under your name, without asking.',
        confirmLabel: `Post as ${who}`,
      }))
    ) {
      return;
    }
    await run(
      c.id,
      () => setChannel({ variables: { id: c.id, mode: mode === 'out' ? 'auto' : 'ask' } }),
      `${name}: ${modeWords(mode)}.`
    );
  };

  const putBack = async (id: string) => {
    try {
      await restore({ variables: { snapshotId: id } });
      showInkToast('Your earlier settings are back.');
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    }
  };

  const toggle = async () => {
    if (allRequireApproval) {
      if (snapshotId) return putBack(snapshotId);
      // Never loosen anything in bulk without a snapshot.
      showInkToast('Everything already asks you first. Loosen one at a time below.');
      return;
    }
    try {
      const { data } = await requireApproval({ variables: { accountId } });
      const id = data?.requireApprovalForEverything?.snapshotId;
      showInkToast(
        'Everything now asks you first.',
        id ? { label: 'Undo', onClick: () => void putBack(id), ms: 6000 } : undefined
      );
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    }
  };

  const lightOf = (id: string) => (lit === id ? 'true' : undefined);

  return (
    <div className="agc">
      {heading}
      <div className="agc-summary" data-boxed="true" data-tall="true">
        <span className="ink-dot agc-dot" data-tone={counted ? 'warn' : 'ok'} aria-hidden />
        <span className="agc-summary-text">
          <strong>{goesOutSentence(items)}</strong>{' '}
          {counted
            ? `${goesOutList(items)}. Everything else asks you first.`
            : 'Agents can still write drafts.'}
        </span>
      </div>
      {canChange ? (
        <button
          type="button"
          className="ink-profile-notif agc-switch"
          role="switch"
          aria-checked={allRequireApproval}
          disabled={tightening || restoring}
          onClick={() => void toggle()}
        >
          <span style={{ flex: 1 }}>
            <span className="ink-profile-notif-name">{ASK_WORDS} for everything</span>
            <span className="ink-profile-notif-desc">
              {allRequireApproval
                ? snapshotId
                  ? 'On. Turn it off to bring back your earlier settings.'
                  : 'On. Nothing goes out without asking. Drafting is still allowed.'
                : `Sets everything below to ${ASK_WORDS} in one step. You can undo it.${
                    social ? ' Campaigns you already approved keep their dates.' : ''
                  }`}
            </span>
          </span>
          <span className="ink-profile-switch" data-on={allRequireApproval ? 'true' : 'false'} />
        </button>
      ) : null}

      <div className="agc-section">
        <div className="agc-section-head">
          <span className="agc-section-name">Agents</span>
          <span className="agc-section-note">What each agent does on your site</span>
        </div>
        {agents.length ? null : <p className="agc-none">No agents connected yet.</p>}
        {agents.map((a) => (
          <div className="agc-agent" key={a.id}>
            <div className="agc-agent-head">
              <span className="agc-agent-tile" aria-hidden>
                {initials(a.name)}
              </span>
              <span className="agc-agent-name">{a.name}</span>
              <Link to={`/settings/ai-agents#agent-card-${a.id}`}>
                AI Agents <span aria-hidden>→</span>
              </Link>
            </div>
            {AGENT_ACTIONS.map(({ key, name, desc }) => {
              const value = a[key];
              const rowId = agentAnchor(a.id, key);
              return (
                <div className="agc-row" key={key} id={rowId} data-lit={lightOf(rowId)}>
                  <div className="agc-row-text">
                    <div className="agc-row-name">{name}</div>
                    <div className="agc-row-sub">{agentDesc(key, desc, value)}</div>
                  </div>
                  {value === 'never' ? (
                    <span className="agc-cant">Can’t do this · set in AI Agents</span>
                  ) : (
                    <ModePair
                      label={`${a.name}: ${name}`}
                      mode={value === 'allow' ? 'out' : 'ask'}
                      canChange={canChange}
                      disabled={busy === rowId}
                      onPick={(next) =>
                        void run(
                          rowId,
                          () =>
                            setAgent({
                              variables: {
                                accountId,
                                dataSourceId: a.id,
                                key,
                                value: next === 'out' ? 'allow' : 'ask',
                              },
                            }),
                          `${a.name}: ${name} set to ${modeWords(next)}.`
                        )
                      }
                    />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {social ? (
        <>
          <div className="agc-section">
            <div className="agc-section-head">
              <span className="agc-section-name">Social channels</span>
              <span className="agc-section-note">Posts your agent writes for each channel</span>
            </div>
            {channels.length ? null : <p className="agc-none">No channels connected yet.</p>}
            {channels.map((c) => {
              const rowId = channelAnchor(c.id);
              return (
                <div className="agc-row" key={c.id} id={rowId} data-lit={lightOf(rowId)}>
                  <ChannelLogo platform={c.platform} className="rc-mark agc-row-logo" />
                  <div className="agc-row-text">
                    <div className="agc-row-name">
                      {channelName(c.platform)}{' '}
                      <span className="agc-row-handle">{c.username || c.name || ''}</span>
                    </div>
                    <div className="agc-row-sub">{channelDesc(c)}</div>
                  </div>
                  <ModePair
                    label={`${channelName(c.platform)} approvals`}
                    mode={c.mode}
                    canChange={canChange}
                    disabled={busy === c.id}
                    onPick={(next) => void pickChannel(c, next)}
                  />
                </div>
              );
            })}
          </div>

          <div className="agc-section">
            <div className="agc-section-head">
              <span className="agc-section-name">Automatic rules</span>
              <span className="agc-section-note">Posts a rule makes on its own</span>
            </div>
            {rules.length ? null : <p className="agc-none">No rules are on.</p>}
            {rules.map((r) => {
              const rowId = ruleAnchor(r.id);
              return (
                <div className="agc-row" key={r.id} id={rowId} data-lit={lightOf(rowId)}>
                  <div className="agc-row-text">
                    <div className="agc-row-name">
                      <Link to={`/reach/automatic?campaign=${r.id}`}>{r.name}</Link>
                    </div>
                    <div className="agc-row-sub">{r.summary}</div>
                  </div>
                  <ModePair
                    label={`${r.name} approvals`}
                    mode={r.mode}
                    canChange={canChange}
                    disabled={busy === r.id}
                    onPick={(next) =>
                      void run(
                        r.id,
                        () => setRule({ variables: { id: r.id, mode: next } }),
                        `${r.name}: ${modeWords(next)}.`
                      )
                    }
                  />
                </div>
              );
            })}
          </div>

          <div className="agc-section">
            <div className="agc-section-head">
              <span className="agc-section-name">Campaigns</span>
              <span className="agc-section-note">
                Always {ASK_WORDS}. You approve a campaign once, then its posts go out on their
                dates.
              </span>
            </div>
            {campaigns.length ? null : (
              <p className="agc-none">No approved campaigns have posts still to go out.</p>
            )}
            {campaigns.map((c) => (
              <div className="agc-row" key={c.id}>
                <div className="agc-row-text">
                  <div className="agc-row-name">{c.name}</div>
                  <div className="agc-row-sub">
                    You approved it. Its posts go out on their dates.
                  </div>
                </div>
                <Link className="ink-bill-link" to={`/campaigns?campaign=${c.id}`}>
                  Open
                </Link>
              </div>
            ))}
          </div>
        </>
      ) : null}

      <p className="agc-foot">
        Every change is kept in the Activity Log with who made it and when.{' '}
        <Link to={PERM_ACTIVITY_PATH}>See changes</Link>
      </p>
    </div>
  );
}
